import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { SignalRService } from 'src/app/shared/services/signalr.service';
import { UserService } from 'src/app/shared/services/user.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MinimizedMeetingService } from '../services/minimized-meeting.service';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { MessageDialogComponent } from 'src/app/shared/components/message-dialog/message-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { AppConstants } from 'src/app/shared/constants/app.contants';

@Component({
  selector: 'app-waiting-attendance-dialog',
  templateUrl: './waiting-attendance-dialog.component.html',
  styleUrls: ['./waiting-attendance-dialog.component.scss']
})
export class WaitingAttendanceDialogComponent implements OnInit, OnDestroy {
  meetingTitle: string = '';
  meetingId: string = '';
  invitedMembers: any[] = []; // Members who are invited to the meeting
  attendees: Array<{ userId: string; userName: string }> = [];
  agendaItems: any[] = []; // Added to pass to next dialog
  private subscriptions: Subscription[] = [];
  private isMinimizing: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<WaitingAttendanceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private dialog: MatDialog,
    private signalRService: SignalRService,
    private minimizedMeetingService: MinimizedMeetingService,
    private userService: UserService,
    private jwtHelper: JwtHelperService
  ) {
    this.meetingTitle = data?.meetingTitle || '';
    this.meetingId = data?.meetingId || '';
    this.invitedMembers = data?.members || [];
  }

  ngOnInit(): void {
    // IMMEDIATELY remove from minimized list when dialog opens (toaster should never show when dialog is open)
    if (this.meetingId) {
      this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
    }
    
    // Get meeting attendees
    if (this.meetingId) {
      this.signalRService.getMeetingAttendees(this.meetingId);
      
      // Listen for attendees updates
      const attendeesSub = this.signalRService.meetingAttendees$.subscribe(
        (attendees) => {
          this.attendees = attendees;
        }
      );
      this.subscriptions.push(attendeesSub);

      // Listen for user joined/left events
      const userJoinedSub = this.signalRService.userJoined$.subscribe(
        (data) => {
          if (!this.attendees.find(a => a.userId === data.userId)) {
            this.attendees.push(data);
            this.signalRService.getMeetingAttendees(this.meetingId);
          }
        }
      );
      this.subscriptions.push(userJoinedSub);

      const userLeftSub = this.signalRService.userLeft$.subscribe(
        (data) => {
          this.attendees = this.attendees.filter(a => a.userId !== data.userId);
        }
      );
      this.subscriptions.push(userLeftSub);

      // Listen for meeting status changed events - close dialog if status changes to OnGoing
      const meetingStatusChangedSub = this.signalRService.meetingStatusChanged$.subscribe(
        (statusData) => {
          // Check if this status change is for this meeting
          const isCurrentMeeting = statusData.meetingId === this.meetingId || 
                                   statusData.meetingIdInt === this.meetingId || 
                                   statusData.id?.toString() === this.meetingId;
          
          if (isCurrentMeeting) {
            const newStatus = statusData.status?.toLowerCase() || '';
            const isOnGoing = newStatus === 'ongoing' || newStatus === '3';
            
            // If status changed to OnGoing, close this dialog
            if (isOnGoing) {
              console.log('Meeting status changed to OnGoing - closing waiting attendance dialog');
              this.dialogRef.close({ action: 'begun' });
            }
          }
        }
      );
      this.subscriptions.push(meetingStatusChangedSub);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Check if a member has joined the meeting
  hasMemberJoined(member: any): boolean {
    if (!member || !this.attendees || this.attendees.length === 0) {
      return false;
    }
    // Check by userId or userName/name match
    return this.attendees.some(attendee => {
      // Match by userId (convert both to string for comparison)
      if (attendee.userId && member.userId && 
          attendee.userId.toString() === member.userId.toString()) {
        return true;
      }
      // Match by userId from member.id
      if (attendee.userId && member.id && 
          attendee.userId.toString() === member.id.toString()) {
        return true;
      }
      // Match by name/userName (case-insensitive)
      const attendeeName = (attendee.userName || '').toLowerCase().trim();
      const memberName = ((member.name || member.userName || '')).toLowerCase().trim();
      if (attendeeName && memberName && attendeeName === memberName) {
        return true;
      }
      return false;
    });
  }

  // Get joined members count
  getJoinedCount(): number {
    return this.invitedMembers.filter(member => this.hasMemberJoined(member)).length;
  }

  // Get not joined members count
  getNotJoinedCount(): number {
    return this.invitedMembers.filter(member => !this.hasMemberJoined(member)).length;
  }

  // Get sorted members list (joined members first, then waiting members)
  getSortedMembers(): any[] {
    if (!this.invitedMembers || this.invitedMembers.length === 0) {
      return [];
    }
    
    // Separate joined and waiting members
    const joinedMembers = this.invitedMembers.filter(member => this.hasMemberJoined(member));
    const waitingMembers = this.invitedMembers.filter(member => !this.hasMemberJoined(member));
    
    // Return joined members first, then waiting members
    return [...joinedMembers, ...waitingMembers];
  }

  onCancel(): void {
    // Minimize instead of close
    this.minimizeDialog();
  }

  minimizeDialog(): void {
    this.isMinimizing = true;
    
    // Add to minimized meetings
    this.minimizedMeetingService.addMinimizedMeeting({
      meetingId: this.meetingId,
      meetingTitle: this.meetingTitle,
      status: 'WaitingForAttendance',
      dialogType: 'waiting',
      dialogData: {
        meetingTitle: this.meetingTitle,
        meetingId: this.meetingId,
        meetingIdInt: this.data?.meetingIdInt || this.data?.id,
        id: this.data?.meetingIdInt || this.data?.id,
        members: this.invitedMembers,
        agendaItems: this.data?.agendaItems || []
      },
      dialogRef: this.dialogRef
    });

    // Close the dialog (will be restored from minimized state)
    this.dialogRef.close({ action: 'minimized' });
  }

  // Check if user is hoster (Secretary)
  isHoster(): boolean {
    let userRole: any = this.userService.user?.Role;
    const userName = this.userService.user?.FullName || '';
    
    // Fallback: Try to read role directly from token if not in user object
    if (!userRole && this.userService.token) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(this.userService.token);
        userRole = decodedToken?.['Role'] || decodedToken?.['role'] || decodedToken?.[AppConstants.CLAIMS.Role];
      } catch (e) {
        console.warn('Could not decode token for role:', e);
      }
    }

    // Check if user is Secretary
    const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
    const isSecretaryRole = userRole === 'Secretary' || userRole === 'secretary' || userRole === 'SECRETARY' || 
                           userRole === 'Tender Committee Secretary' || roleValue === 4;
    
    // Check if user is in members list with Secretary role
    const isSecretaryInMembers = this.invitedMembers?.some((m: any) => 
      (m.name && userName && m.name.toLowerCase() === userName.toLowerCase()) && 
      (m.role === 'Secretary' || m.role === 'secretary' || m.role === 'SECRETARY' || m.role === 'Tender Committee Secretary' || m.role === 4)
    );
    
    return isSecretaryRole || isSecretaryInMembers;
  }

  // Check if user is TCM member
  isTCM(): boolean {
    let userRole: any = this.userService.user?.Role;
    
    // Fallback: Try to read role directly from token if not in user object
    if (!userRole && this.userService.token) {
      try {
        const decodedToken = this.jwtHelper.decodeToken(this.userService.token);
        userRole = decodedToken?.['Role'] || decodedToken?.['role'] || decodedToken?.[AppConstants.CLAIMS.Role];
      } catch (e) {
        console.warn('Could not decode token for role:', e);
      }
    }

    // Check if user is TCM (not hoster)
    const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
    const isTCMRole = userRole === 'TCM' || userRole === 'tcm' || userRole === 'TCM' || roleValue === 1;
    
    // TCM members are those who are not hosters
    return isTCMRole && !this.isHoster();
  }

  // End meeting (for Secretary only)
  async endMeeting(): Promise<void> {
    const meetingIdInt = this.data?.meetingIdInt || this.data?.id;

    if (!meetingIdInt) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: 'Meeting ID is missing. Cannot end meeting.',
          type: 'error'
        }
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'End Meeting',
        message: 'Are you sure you want to end this meeting? This will cancel the meeting and change its status to Cancelled.',
        confirmText: 'Yes, End Meeting',
        cancelText: 'Cancel'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this._loaderService.showLoader();
    try {
      // Call backend API to cancel the meeting
      const url = `${environment.API_URL}/api/Meetings/${meetingIdInt}/cancel`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess) {
        // First update the status to Cancelled so the toaster filters it out immediately
        this.minimizedMeetingService.updateMeetingStatus(this.meetingId, 'Cancelled');
        
        // Then remove from minimized list immediately
        this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);
        
        // Also remove any meetings with Cancelled status from minimized list (in case of duplicates)
        this.minimizedMeetingService.removeMeetingsByStatus(['Cancelled', '4', 'cancelled']);
        
        // Broadcast end meeting event via SignalR
        if (this.meetingId) {
          try {
            await this.signalRService.endMeeting(this.meetingId);
          } catch (error) {
            console.error('Error broadcasting meeting ended event:', error);
          }
        }

        // Close the dialog
        this.dialogRef.close({ action: 'ended' });
      } else {
        console.error('Failed to end meeting:', response?.errorMessageEn);
        this.dialog.open(MessageDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: response?.errorMessageEn || 'Failed to end meeting. Please try again.',
            type: 'error'
          }
        });
      }
    } catch (error: any) {
      console.error('Error ending meeting:', error);
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: error?.error?.errorMessageEn || 'An error occurred while ending the meeting. Please try again.',
          type: 'error'
        }
      });
    } finally {
      this._loaderService.hideLoader();
    }
  }

  // Check if current user has joined the meeting
  hasCurrentUserJoined(): boolean {
    const userId = this.userService.user?.Id?.toString() || '';
    const userName = this.userService.user?.FullName || '';
    
    if (!userId && !userName) {
      return false;
    }

    return this.attendees.some(attendee => {
      // Match by userId
      if (attendee.userId && userId && attendee.userId.toString() === userId) {
        return true;
      }
      // Match by userName (case-insensitive)
      const attendeeName = (attendee.userName || '').toLowerCase().trim();
      const currentUserName = userName.toLowerCase().trim();
      if (attendeeName && currentUserName && attendeeName === currentUserName) {
        return true;
      }
      return false;
    });
  }

  // Attend meeting (for TCM members - marks them as joined)
  async attendMeeting(): Promise<void> {
    if (!this.meetingId) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: 'Meeting ID is missing. Cannot attend meeting.',
          type: 'error'
        }
      });
      return;
    }

    try {
      const userId = this.userService.user?.Id?.toString() || '';
      const userName = this.userService.user?.FullName || 'User';
      
      if (!userId) {
        this.dialog.open(MessageDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: 'User ID is missing. Cannot attend meeting.',
            type: 'error'
          }
        });
        return;
      }

      // Join the meeting via SignalR (this will mark them as attending)
      await this.signalRService.joinMeeting(this.meetingId, userId, userName);
      
      // Refresh attendees list
      this.signalRService.getMeetingAttendees(this.meetingId);
      
      // Show success message
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Success',
          message: 'You have successfully joined the meeting.',
          type: 'success'
        }
      });
    } catch (error: any) {
      console.error('Error attending meeting:', error);
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: error?.error?.errorMessageEn || 'An error occurred while joining the meeting. Please try again.',
          type: 'error'
        }
      });
    }
  }

  // Leave meeting (for all members except host)
  async leaveMeeting(): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Leave Meeting',
        message: 'Are you sure you want to leave this meeting?',
        confirmText: 'Yes, Leave',
        cancelText: 'Cancel'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    // Leave the SignalR meeting room
    if (this.meetingId) {
      try {
        const userId = this.userService.user?.Id?.toString() || '';
        await this.signalRService.leaveMeeting(this.meetingId, userId);
      } catch (error) {
        console.error('Error leaving meeting:', error);
      }
    }

    // Remove from minimized list
    this.minimizedMeetingService.removeMinimizedMeeting(this.meetingId);

    // Close the dialog
    this.dialogRef.close({ action: 'left' });
  }

  // Begin meeting (change from WaitingForAttendance to OnGoing)
  async beginMeeting(): Promise<void> {
    const meetingIdInt = this.data?.meetingIdInt || this.data?.id;

    if (!meetingIdInt) {
      this.dialog.open(MessageDialogComponent, {
        width: '400px',
        data: {
          title: 'Error',
          message: 'Meeting ID is missing. Cannot begin meeting.',
          type: 'error'
        }
      });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Start Meeting',
        message: 'Are you sure you want to start this meeting? This will change the status to OnGoing.',
        confirmText: 'Yes, Start',
        cancelText: 'Cancel'
      }
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/Meetings/${meetingIdInt}/begin`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, {}));

      if (response?.isSuccess) {
        // Close the waiting-attendance-dialog
        // The backend will broadcast the status change and OpenMeetingSession event
        // The frontend will automatically handle closing dialogs and opening new ones
        this.dialogRef.close({ action: 'begun' });
      } else {
        console.error('Failed to begin meeting:', response?.errorMessageEn);
        // Show validation error message from backend
        const errorMessage = response?.errorMessageEn || response?.errorMessage || 'Failed to begin meeting. Please try again.';
        this.dialog.open(MessageDialogComponent, {
          width: '450px',
          data: {
            title: 'Cannot Start Meeting',
            message: errorMessage,
            type: 'error'
          }
        });
      }
    } catch (error: any) {
      console.error('Error beginning meeting:', error);
      // Extract error message from various possible error response formats
      let errorMessage = 'An error occurred while beginning the meeting. Please try again.';
      
      if (error?.error?.errorMessageEn) {
        errorMessage = error.error.errorMessageEn;
      } else if (error?.error?.errorMessage) {
        errorMessage = error.error.errorMessage;
      } else if (error?.errorMessageEn) {
        errorMessage = error.errorMessageEn;
      } else if (error?.errorMessage) {
        errorMessage = error.errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      this.dialog.open(MessageDialogComponent, {
        width: '450px',
        data: {
          title: 'Cannot Start Meeting',
          message: errorMessage,
          type: 'error'
        }
      });
    } finally {
      this._loaderService.hideLoader();
    }
  }
}

